import React, {useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getCurrencyCode, getMeta} from 'modules/meta/selectors'
import {getIsRtlLanguage} from 'modules/meta/selectors'
import {imageProvider} from 'utils/imageProvider'
import {v4 as uuid} from 'uuid'

import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  HotelContext,
  TrackEventPayload
} from '@vio/core/src/analytics/types/Events'
import {getCashback, getHotelPrices} from '@vio/core/src/carouselWidgets/utils'
import {SearchApiError} from '@vio/core/src/sapi/services/searchApi'
import {
  SuggestionHotelCard,
  SuggestionHotelCardSkeleton
} from '@vio/shared/src/search/SuggestionHotelCard'

import {SimilarHotel} from '../types'
import {useGetDealsForTonight} from './hooks/useGetDealsForTonight'

const SkeletonContainer = styled.div`
  display: flex;
  overflow: hidden;
  padding-left: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s500};
  > div + div {
    margin-left: ${cssTheme.layout.spacing.s500};
  }
  ${linariaMq.desktopXs} {
    padding-left: 0;
    padding-right: 0;
  }
`

interface HotelsListProps {
  hotelId?: string
  placeId?: string
  rooms: string
  checkIn: string
  checkOut: string
  trackingPayload: Pick<TrackEventPayload, 'component' | 'payload' | 'team'>
  onHotelClick: ({hotelId}) => void
  hasInsideMargin?: boolean
  isFetchingGeo?: boolean
  onRequestStart?: () => void
  onRequestComplete?: () => void
  shouldWaitRequestComplete?: boolean
  hasSidePaddings: boolean
  numberOfResults?: number
  selectedPlaceId: string
}

interface HotelItemProps
  extends Pick<
    HotelsListProps,
    'checkIn' | 'checkOut' | 'trackingPayload' | 'hasInsideMargin'
  > {
  similarHotel: SimilarHotel
  position: number
  onClick: ({hotelId}) => void
}

const HotelItem = ({
  similarHotel,
  trackingPayload,
  hasInsideMargin,
  checkIn,
  checkOut,
  onClick,
  position
}: HotelItemProps) => {
  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const currencyCode = useSelector(getCurrencyCode)
  const dispatch = useDispatch()
  const {
    monetisation: {cashback: cashbackCapability}
  } = useBrandCapabilities()

  if (!similarHotel || !similarHotel?.hotelEntity) {
    return null
  }

  const {hotelEntity, offerEntity} = similarHotel
  const {hotelName, reviewCount, starRating, imageURIs, guestRating, objectID} =
    hotelEntity

  const {price, anchorPrice} = getHotelPrices({
    offerEntity,
    checkIn,
    checkOut,
    includeTaxes,
    includeLocalTaxes
  })
  const {cashback} = getCashback(offerEntity)

  const onHotelClick = () => {
    const hotelContext = {
      hotelId: objectID,
      hotelPosition: position
    } as HotelContext

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelCard,
        action: Action.Clicked,
        analyticsContext: {
          [AnalyticsContext.HotelContext]: hotelContext
        },
        ...trackingPayload
      })
    )

    onClick({hotelId: objectID})
  }

  return (
    <SuggestionHotelCard
      key={objectID}
      hotelName={hotelName}
      starRating={starRating}
      reviewRating={guestRating?.overall}
      reviewRatingCount={reviewCount}
      imageUrl={imageURIs?.[0]}
      currency={currencyCode}
      price={price}
      anchorPrice={anchorPrice}
      onHotelClick={onHotelClick}
      imageProvider={imageProvider}
      hasInsideMargin={hasInsideMargin}
      cashback={cashbackCapability.enabled ? cashback : undefined}
    />
  )
}

export const HotelsListSkeleton = () => (
  <SkeletonContainer>
    {[...Array(5)].map((_, index) => (
      <SuggestionHotelCardSkeleton key={index} />
    ))}
  </SkeletonContainer>
)

export const HotelsList = ({
  hotelId,
  placeId,
  rooms,
  checkIn,
  checkOut,
  trackingPayload,
  onHotelClick,
  hasInsideMargin,
  hasSidePaddings,
  isFetchingGeo = false,
  onRequestStart,
  onRequestComplete,
  shouldWaitRequestComplete,
  numberOfResults = 10,
  selectedPlaceId
}: HotelsListProps) => {
  const instanceId = useRef(uuid())
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  const dispatch = useDispatch()

  const [requestComplete, setRequestComplete] = useState(false)

  const handleRequestStart = id => {
    if (id !== instanceId.current) return
    setRequestComplete(false)
    onRequestStart?.()
  }

  const handleRequestComplete = id => {
    if (id !== instanceId.current) return
    setRequestComplete(true)
    onRequestComplete?.()
  }

  const handleRequestError = (id: string, error: Error) => {
    if (id !== instanceId.current) return

    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.Search,
        action: Action.Errored,
        payload: {
          sourceComponent: 'HotelsList'
        },
        analyticsContext: {
          [AnalyticsContext.ErrorContext]: {
            errorType: 'GetDealsFailed',
            errorMessage: (error as Error).message
          }
        }
      })
    )

    if (typeof Sentry !== 'undefined')
      Sentry.captureException(
        new SearchApiError('SuggestedHotels: Error fetching offers', error),
        {extra: trackingPayload}
      )
  }

  const {hotelIds, hotels, hotelsReceived} = useGetDealsForTonight({
    placeId,
    hotelId,
    rooms,
    checkIn,
    checkOut,
    instanceId: instanceId.current,
    onRequestStart: handleRequestStart,
    onRequestComplete: handleRequestComplete,
    onRequestError: handleRequestError,
    numberOfResults
  })

  if (
    (shouldWaitRequestComplete && !requestComplete) ||
    !hotelsReceived ||
    isFetchingGeo
  ) {
    return selectedPlaceId === placeId ? <HotelsListSkeleton /> : null
  }

  return selectedPlaceId === placeId ? (
    <SmoothScrollNavigation
      isRtlLanguage={isRtlLanguage}
      hasSidePaddings={hasSidePaddings}
    >
      {hotelIds?.map((hotelId, index) => (
        <HotelItem
          key={hotelId}
          similarHotel={hotels?.[hotelId]}
          trackingPayload={{
            ...trackingPayload,
            payload: {
              totalNumber: numberOfResults,
              index: index + 1,
              searchItem: {
                hotelId,
                checkIn,
                checkOut,
                rooms
              }
            }
          }}
          checkIn={checkIn}
          checkOut={checkOut}
          position={index + 1}
          hasInsideMargin={hasInsideMargin}
          onClick={onHotelClick}
        />
      ))}
    </SmoothScrollNavigation>
  ) : null
}
